import * as moment from 'moment';
import { Company } from './company';
import { Dependent } from './dependent';
import { Funder } from './funder';
import { Scheme } from './scheme';
import { User } from './user';
import { getCurrentApiDomain } from '../helpers/helper-functions';
import { Agent } from './agent';

export class Member {

  company_number?: string;
  member_number!: string;
  scheme_code!: string;
  date_joined!: number;
  // Path B: NULL until first APPROVED contribution receipt is processed.
  effective_date?: number | null;
  date_approved?: number;
  membership_status!: string;
  funder_membership_status!: string;
  company_membership_status!: string;
  old_member_number?: string;
  membership_type!: string;
  mobile_money_service?: string;
  mobile_money_phone_number?: string;
  bank_name?: string;
  bank_account_name?: string;
  bank_branch?: string;
  bank_account_number?: string;
  payment_method?: string;
  billing_period!: string;
  sponsor_full_name?: string;
  sponsor_relationship?: string;
  sponsor_national_id_number?: string;
  sponsor_physical_address?: string;
  sponsor_phone_number?: string;
  notes?: string;
  // Stage 4 PR 4, 2026-05-13 — signed percentage adjusting every
  // invoice line. Positive = loading (surcharge), negative = discount.
  // Defaults to 0; corporate members inherit company's value at create.
  discount_or_loading_percentage?: number;
  date_deleted?: number;
  uuid!: string;
  user_uuid!: string;
  date_created!: number;
  date_updated!: number;

  // 2026-07-03 — company-initiated scheme-change request (maker-checker).
  // The company stashes the requested SPECIAL scheme here + raises a funder
  // task; the real scheme_code is untouched until the funder approves.
  requested_scheme_code?: string | null;
  scheme_change_request_scope?: string | null;
  has_pending_scheme_change_request?: boolean;
  requested_scheme?: Scheme | null;                    // attached on show when pending
  requested_scheme_within_contract?: boolean | null;   // null = no contract on file (judgment mode)
  has_pending_termination_request?: boolean;           // WAVE 🔴 #10 — attached on show when pending

  user?: User;
  principal_dependent?: Dependent;
  company?: Company;
  agent?: Agent;
  dependents?: Array<Dependent>;
  scheme?: Scheme;

  hasDependents() {
    return this.getDependents()?.length ?? 0 > 0;
  }

  getDependents() {
    return this.dependents?.filter(
      (el) => el.relationship.toLocaleLowerCase() != 'principal_member'
    );
  }

  hasPendingSchemeChangeRequest(): boolean {
    return this.has_pending_scheme_change_request === true;
  }

  hasPendingTerminationRequest(): boolean {
    return this.has_pending_termination_request === true;
  }

  apiURL = getCurrentApiDomain('');


  getWelcomeLetterPreviewUrl() {
    return this.apiURL + '/member-welcome-letter/preview/' + this.uuid;
  }

  getTerminationLetterPreviewUrl() {
    return this.apiURL + '/member-termination-letter/preview/' + this.uuid;
  }

  getMembershipCertificatePreviewUrl() {
    return this.apiURL + '/membership-certificate/preview/' + this.uuid;
  }


  isPendingOrDraft() {
    return this.isDraft() || this.isPending();
  }


  isDraft() {
    return this.membership_status.toLocaleLowerCase() == 'draft';
  }

  isActive() {
    return this.membership_status.toLocaleLowerCase() == 'active';
  }

  isPending() {
    return this.membership_status.toLocaleLowerCase() == 'pending';
  }

  isPendingPayment() {
    return this.membership_status.toLocaleLowerCase() == 'pending_payment';
  }

  isSuspended() {
    return this.membership_status.toLocaleLowerCase() == 'suspended';
  }

  isLapsed() {
    return this.membership_status.toLocaleLowerCase() == 'lapsed';
  }


  companyStatusIsActive() {
    return this.company_membership_status.toLocaleLowerCase() == 'active';
  }

  companyStatusIsPending() {
    return this.company_membership_status.toLocaleLowerCase() == 'pending';
  }

  companyStatusIsSuspended() {
    return this.company_membership_status.toLocaleLowerCase() == 'suspended';
  }

  isTerminated() {
    return this.membership_status.toLocaleLowerCase() == 'terminated';
  }

  isIndividual() {
    return this.membership_type.toLocaleLowerCase() == 'individual';
  }

  isCorporate() {
    return this.membership_type.toLocaleLowerCase() == 'corporate';
  }

  constructor(data: any) {
    this.company_number = data.company_number;
    this.member_number = data.member_number;
    this.scheme_code = data.scheme_code;
    this.date_joined = data.date_joined;
    this.effective_date = data.effective_date;
    this.date_approved = data.date_approved;
    this.membership_status = data.membership_status;
    this.funder_membership_status = data.funder_membership_status;
    this.company_membership_status = data.company_membership_status;
    this.old_member_number = data.old_member_number;
    this.membership_type = data.membership_type;
    this.notes = data.notes;
    this.discount_or_loading_percentage = data.discount_or_loading_percentage;
    this.sponsor_full_name = data.sponsor_full_name;
    this.sponsor_relationship = data.sponsor_relationship;
    this.sponsor_national_id_number = data.sponsor_national_id_number;
    this.sponsor_physical_address = data.sponsor_physical_address;
    this.sponsor_phone_number = data.sponsor_phone_number;
    this.mobile_money_service = data.mobile_money_service;
    this.mobile_money_phone_number = data.mobile_money_phone_number;
    this.bank_name = data.bank_name;
    this.bank_account_name = data.bank_account_name;
    this.bank_branch = data.bank_branch;
    this.bank_account_number = data.bank_account_number;
    this.payment_method = data.payment_method;
    this.billing_period = data.billing_period;
    this.date_deleted = data.date_deleted;
    this.uuid = data.uuid;
    this.user_uuid = data.user_uuid;
    this.date_created = data.date_created;
    this.date_updated = data.date_updated;

    this.requested_scheme_code = data.requested_scheme_code;
    this.scheme_change_request_scope = data.scheme_change_request_scope;
    this.has_pending_scheme_change_request = data.has_pending_scheme_change_request;
    this.has_pending_termination_request = data.has_pending_termination_request;
    this.requested_scheme_within_contract = data.requested_scheme_within_contract;

    if (data.requested_scheme) {
      this.requested_scheme = new Scheme(data.requested_scheme);
    }
    if (data.user) {
      this.user = new User(data.user);
    }
    if (data.principal_dependent) {
      this.principal_dependent = new Dependent(data.principal_dependent);
    }
    if (data.company) {
      this.company = new Company(data.company);
    }
    if (data.agent) {
      this.agent = new Agent(data.agent);
    }
    if (data.dependents) {
      this.dependents = data.dependents.map((el: any) => new Dependent(el));
    }
    if (data.scheme) {
      this.scheme = new Scheme(data.scheme);
    }
  }
}
